// Anchors a passage of rendered text by quoting it: the quote plus a little context on each side
// is enough to find the same passage again after a re-render, so nothing has to be written into
// the content itself (block chapters are generated JSON, not an editable document).
export type TTextQuoteAnchor = { quote: string; prefix: string; suffix: string; sectionId?: string };

export const ANCHOR_CONTEXT_LENGTH = 32;
export const MAX_ANCHOR_QUOTE_LENGTH = 2000;

const SKIPPED_PARENTS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA"]);
// Text in different blocks (or chart labels, each an SVG text element) reads as separate words,
// like Selection.toString(), so a boundary between them counts as a space even though no text
// node holds one.
const BLOCK_SELECTOR =
  "p,div,li,tr,td,th,h1,h2,h3,h4,h5,h6,section,article,blockquote,figure,figcaption,ul,ol,table,thead,tbody,pre,dt,dd,text";

// Whitespace-collapsed text of a subtree; rawOffsets maps each collapsed character back to its
// position in the raw concatenation of the text nodes, nodeStarts gives each node's raw start.
type TTextIndex = { text: string; rawOffsets: number[]; nodes: Text[]; nodeStarts: number[] };

export const collapseWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

function buildTextIndex(root: Node): TTextIndex {
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  const nodeStarts: number[] = [];
  const chars: string[] = [];
  const rawOffsets: number[] = [];
  let raw = 0;
  let previousBlock: Element | null = null;
  let pendingBreak = false;
  const pushChar = (char: string, rawOffset: number) => {
    if (char === " " && (chars.length === 0 || chars[chars.length - 1] === " ")) return;
    chars.push(char);
    rawOffsets.push(rawOffset);
  };
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType !== Node.TEXT_NODE) {
      if ((node as Element).tagName === "BR") pendingBreak = true;
      continue;
    }
    const textNode = node as Text;
    const parent = textNode.parentElement;
    if (SKIPPED_PARENTS.has(parent?.tagName ?? "")) continue;
    const block = parent?.closest(BLOCK_SELECTOR) ?? null;
    if (pendingBreak || (previousBlock !== null && block !== previousBlock)) pushChar(" ", raw);
    pendingBreak = false;
    previousBlock = block;
    nodes.push(textNode);
    nodeStarts.push(raw);
    const value = textNode.nodeValue ?? "";
    for (let i = 0; i < value.length; i += 1) {
      pushChar(/\s/.test(value[i]) ? " " : value[i], raw + i);
    }
    raw += value.length;
  }
  return { text: chars.join(""), rawOffsets, nodes, nodeStarts };
}

// First index whose value is >= target (values ascending).
function lowerBound(values: number[], target: number) {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (values[mid] < target) low = mid + 1;
    else high = mid;
  }
  return low;
}

const totalRawLength = (index: TTextIndex) => {
  const last = index.nodes.length - 1;
  return last < 0 ? 0 : index.nodeStarts[last] + index.nodes[last].length;
};

const isSameOrAfter = (reference: Node, node: Node) =>
  reference === node ||
  (reference.compareDocumentPosition(node) &
    (Node.DOCUMENT_POSITION_FOLLOWING | Node.DOCUMENT_POSITION_CONTAINED_BY)) !==
    0;

// Raw offset of a DOM boundary point: an offset into a text node, or a child index of an element.
function positionToRaw(index: TTextIndex, container: Node, offset: number): number | null {
  if (container.nodeType === Node.TEXT_NODE) {
    const k = index.nodes.indexOf(container as Text);
    return k === -1 ? null : index.nodeStarts[k] + Math.min(offset, (container as Text).length);
  }
  const child = container.childNodes[offset];
  if (child) {
    const k = index.nodes.findIndex((node) => isSameOrAfter(child, node));
    return k === -1 ? totalRawLength(index) : index.nodeStarts[k];
  }
  for (let k = index.nodes.length - 1; k >= 0; k -= 1) {
    if (container.contains(index.nodes[k])) return index.nodeStarts[k] + index.nodes[k].length;
  }
  const k = index.nodes.findIndex((node) => isSameOrAfter(container, node));
  return k === -1 ? totalRawLength(index) : index.nodeStarts[k];
}

function rawToPosition(index: TTextIndex, rawOffset: number, preferPreviousNode: boolean) {
  let k = Math.max(0, lowerBound(index.nodeStarts, rawOffset + 1) - 1);
  if (preferPreviousNode && k > 0 && index.nodeStarts[k] === rawOffset) k -= 1;
  const node = index.nodes[k];
  return { node, offset: Math.max(0, Math.min(rawOffset - index.nodeStarts[k], node.length)) };
}

function trimOffsets(text: string, start: number, end: number) {
  let from = start;
  let to = end;
  while (from < to && text[from] === " ") from += 1;
  while (to > from && text[to - 1] === " ") to -= 1;
  return { start: from, end: to };
}

export function describeSelectionAnchor(root: Node, range: Range): Omit<TTextQuoteAnchor, "sectionId"> | null {
  const index = buildTextIndex(root);
  const rawStart = positionToRaw(index, range.startContainer, range.startOffset);
  const rawEnd = positionToRaw(index, range.endContainer, range.endOffset);
  if (rawStart === null || rawEnd === null || rawEnd <= rawStart) return null;
  const { start, end } = trimOffsets(
    index.text,
    lowerBound(index.rawOffsets, rawStart),
    lowerBound(index.rawOffsets, rawEnd)
  );
  if (end <= start) return null;
  const quote = index.text.slice(start, Math.min(end, start + MAX_ANCHOR_QUOTE_LENGTH));
  const quoteEnd = start + quote.length;
  return {
    quote,
    prefix: index.text.slice(Math.max(0, start - ANCHOR_CONTEXT_LENGTH), start),
    suffix: index.text.slice(quoteEnd, quoteEnd + ANCHOR_CONTEXT_LENGTH)
  };
}

function contextScore(text: string, start: number, end: number, anchor: TTextQuoteAnchor) {
  let score = 0;
  for (let i = 1; i <= anchor.prefix.length && start - i >= 0; i += 1) {
    if (text[start - i] !== anchor.prefix[anchor.prefix.length - i]) break;
    score += 1;
  }
  for (let i = 0; i < anchor.suffix.length && end + i < text.length; i += 1) {
    if (text[end + i] !== anchor.suffix[i]) break;
    score += 1;
  }
  return score;
}

// Offsets of the quote in whitespace-collapsed text; among repeated occurrences the one whose
// surroundings match the anchor's context best wins.
export function findQuoteOffsets(text: string, anchor: TTextQuoteAnchor): { start: number; end: number } | null {
  const quote = collapseWhitespace(anchor.quote);
  if (!quote) return null;
  let best: number | null = null;
  let bestScore = -1;
  for (let at = text.indexOf(quote); at !== -1; at = text.indexOf(quote, at + 1)) {
    const score = contextScore(text, at, at + quote.length, anchor);
    if (score > bestScore) {
      best = at;
      bestScore = score;
    }
  }
  return best === null ? null : { start: best, end: best + quote.length };
}

const sectionScope = (root: HTMLElement, sectionId: string | undefined) =>
  sectionId ? root.querySelector(`[data-section-id="${sectionId.replace(/["\\]/g, "\\$&")}"]`) : null;

// The anchored section is searched first, then the whole root: a section the content no longer
// has does not lose the comment as long as the passage still exists somewhere in the chapter.
export function resolveTextQuoteAnchor(root: HTMLElement, anchor: TTextQuoteAnchor): Range | null {
  const scopes = [sectionScope(root, anchor.sectionId), root].filter((scope): scope is Element => !!scope);
  for (const scope of scopes) {
    const index = buildTextIndex(scope);
    const found = findQuoteOffsets(index.text, anchor);
    if (!found) continue;
    const start = rawToPosition(index, index.rawOffsets[found.start], false);
    const end = rawToPosition(index, index.rawOffsets[found.end - 1] + 1, true);
    const range = (root.ownerDocument ?? document).createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  }
  return null;
}
